import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

/**
 * Module that updates the `--lerp` CSS variable on elements
 * based on current scroll velocity.
 */
export class StringLerp extends StringModule {
  private hasInitializedCSS = false;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lerp";
  }

  /**
   * Initialize lerp value when object connects.
   */
  onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    // Initialize with 0
    object.setProperty<number>("lerp", 0);
  }

  /**
   * Called on resize - use this to apply initial values after mirrors are created.
   */
  onResize(): void {
    super.onResize();
    // Only apply initial CSS on first resize (after mirrors are created)
    if (!this.hasInitializedCSS && this.objectsOnPage.length > 0) {
      this.hasInitializedCSS = true;
      styleTxn.run(() => {
        this.objectsOnPage.forEach((object) => {
          this.updateObjectLerp(object, true);
        });
      });
    }
  }

  /**
   * Resets the `--lerp` value to 0 when scroll stops.
   */
  onScrollStop(): void {
    this.objects.forEach((object) => {
      this.recomputeLerp(object, 0);
    });
  }

  /**
   * Updates `--lerp` value for each connected object during scroll.
   */
  onFrame(data: StringData): void {
    const velocity = data.scroll.lerped;
    this.objects.forEach((object) => {
      this.recomputeLerp(object, velocity);
    });
  }

  /**
   * Computes the lerp value for the object.
   */
  private recomputeLerp(object: StringObject, velocity: number): void {
    const prevLerp = object.getProperty<number>("lerp");

    if (prevLerp === velocity) {
      return;
    }

    object.setProperty<number>("lerp", velocity);
    // Update immediately when lerp value changes
    this.updateObjectLerp(object);
  }

  /**
   * Applies the lerp value to the object and its mirrors.
   */
  onMutate(): void {
    styleTxn.run(() => {
      this.objects.forEach((object) => {
        this.updateObjectLerp(object);
      });
    });
  }

  /**
   * Updates the CSS variable on the object and connected elements.
   */
  private updateObjectLerp(object: StringObject, force = false): void {
    const lerp = object.getProperty<number>("lerp");
    const prevLerp = object.getProperty<number>("lerp-applied");

    if (!force && prevLerp === lerp) {
      return;
    }

    object.setProperty<number>("lerp-applied", lerp);

    const lerpStr = lerp?.toString() ?? "0";

    this.events.emit(`object:lerp:${object.id}`, lerp);

    styleTxn.run(() => {
      this.applyToElementAndConnects(
        object,
        (el) => {
          styleTxn.setVars(el, { "--lerp": lerpStr });
        },
        (el: HTMLElement, mirror?: StringMirrorObject) => {
          if (!mirror) return;
          styleTxn.setVars(el, { "--lerp": lerpStr });
        }
      );
    });
  }

  /**
   * Cleans up the CSS variable when object is disconnected.
   */
  onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);

    const clearCustomProperty = (el: HTMLElement) => {
      el.style.removeProperty("--lerp");
    };

    clearCustomProperty(object.htmlElement);
    object.mirrorObjects.forEach((mirror) => {
      clearCustomProperty(mirror.htmlElement);
    });
  }
}
